import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Badge,
  Collapse,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaEllipsisV, FaTrash } from "react-icons/fa";
import { ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import dayjs from "dayjs";
import {
  KYDHold,
  KYDTicketTypeAccessCode,
  KYDTicketTypePool,
} from "@common/interfaces/KYDTicket";
import React, { useState, useEffect } from "react";
import ShowIfHasPermission from "src/Manager/ShowIfHasPermission";
import {
  KYDAdminEvent,
  KYDAdminEventDetailSectionPermissions,
} from "@common/interfaces/KYDEvent";
import { showSuccessToast } from "src/utils";
import { CopyIcon } from "@chakra-ui/icons";

interface TicketHoldsTableProps {
  eventLink: string;
  ticketHolds: KYDHold[];
  ticketTypePools: KYDTicketTypePool[];
  accessCodes: KYDTicketTypeAccessCode[];
  onCreateHold: () => void;
  onEditHold?: (hold: KYDHold) => void;
  onReleaseHold?: (hold: KYDHold) => void;
  permissions: KYDAdminEventDetailSectionPermissions;
}

const TicketHoldsTable = ({
  eventLink,
  ticketHolds,
  ticketTypePools,
  onCreateHold,
  onEditHold,
  onReleaseHold,
  accessCodes = [],
  permissions = [],
}: TicketHoldsTableProps) => {
  const { isOpen, onToggle } = useDisclosure();
  const [releaseDialog, setReleaseDialog] = useState<{
    open: boolean;
    hold?: KYDHold;
  }>({ open: false });
  const [releaseLoading, setReleaseLoading] = useState(false);
  const toast = useToast();

  // Helper to get ticket type name
  const getTicketTypeName = (ticket_type_id: string) => {
    const tt = ticketTypePools?.find((t) =>
      t.ticket_types.includes(ticket_type_id)
    );
    return tt ? tt.name : ticket_type_id;
  };

  // Helper to format release date
  const formatReleaseDate = (expires_at?: string) => {
    if (!expires_at) return "—";
    return dayjs(expires_at).format("ddd MMM D, h:mmA");
  };

  // Helper to compute expires_in badge
  const getExpiresIn = (expires_at?: string) => {
    if (!expires_at) return undefined;
    const now = dayjs();
    const exp = dayjs(expires_at);
    if (exp.isAfter(now)) {
      return `EXPIRES IN ${now.to(exp, true)}`;
    }
    return undefined;
  };

  const holds = (ticketHolds || []).filter((h) => h.status === "OPEN");
  const pastHolds = (ticketHolds || []).filter((h) => h.status !== "OPEN");

  return (
    <Box w="full">
      <HStack justify="space-between" mb={4}>
        <Text fontWeight="bold" fontSize="lg">
          Holds
        </Text>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <IconButton
            variant={"outline"}
            aria-label="Create hold"
            icon={<FaPlus />}
            onClick={onCreateHold}
          />
        </ShowIfHasPermission>
      </HStack>
      {holds.length > 0 || pastHolds.length > 0 ? (
        <Table variant="simple" size="md">
          <Thead>
            <Tr>
              <Th w="200px">Label</Th>
              <Th w="200px">Ticket Pool</Th>
              <Th w="150px">Sold</Th>
              <Th>Release Date</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {holds.map((hold) => (
              <Tr key={hold.id}>
                <Td>{hold.name}</Td>
                <Td>
                  <Badge colorScheme="gray">
                    {getTicketTypeName(hold.ticket_type_id)}
                  </Badge>
                </Td>
                <Td>{hold.display?.quantity || "-"}</Td>
                <Td>{formatReleaseDate(hold.expires_at)}</Td>
                <Td>
                  <HStack>
                    <Badge
                      colorScheme={hold.status === "OPEN" ? "green" : "gray"}
                    >
                      {hold.status}
                    </Badge>
                    {getExpiresIn(hold.expires_at) && (
                      <Badge colorScheme="orange">
                        {getExpiresIn(hold.expires_at)}
                      </Badge>
                    )}
                  </HStack>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<FaEllipsisV />}
                      variant="ghost"
                      size="sm"
                    />
                    <MenuList>
                      {hold.access_code_id &&
                        (() => {
                          const accessCode = accessCodes.find(
                            (ac) => ac.id === hold.access_code_id
                          );
                          if (!accessCode) return null;
                          return (
                            <MenuItem
                              icon={<CopyIcon />}
                              key={accessCode.id}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${eventLink}?code=${accessCode.code}`
                                );
                                showSuccessToast(
                                  toast,
                                  "Link copied to clipboard"
                                );
                              }}
                            >
                              Copy Link
                            </MenuItem>
                          );
                        })()}
                      {onEditHold && (
                        <MenuItem
                          icon={<EditIcon />}
                          onClick={() => onEditHold(hold)}
                        >
                          Edit
                        </MenuItem>
                      )}
                      {onReleaseHold && hold.status === "OPEN" && (
                        <MenuItem
                          icon={<FaTrash />}
                          color="red.500"
                          onClick={() => setReleaseDialog({ open: true, hold })}
                        >
                          Release
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text color="gray.500">No holds created</Text>
      )}
      <Box display={pastHolds.length > 0 ? "block" : "none"} mt={2}>
        <Button
          variant="link"
          size="sm"
          onClick={onToggle}
          leftIcon={<ChevronDownIcon />}
        >
          Past Holds ({pastHolds.length})
        </Button>
        <Collapse in={isOpen} animateOpacity>
          <Table variant="simple" size="md" mt={2}>
            <Tbody>
              {pastHolds.map((hold) => (
                <Tr key={hold.id}>
                  <Td w="200px">{hold.name}</Td>
                  <Td w="200px">
                    <Badge colorScheme="gray">
                      {getTicketTypeName(hold.ticket_type_id)}
                    </Badge>
                  </Td>
                  <Td w="150px">{hold.display?.quantity || "-"}</Td>
                  <Td>{formatReleaseDate(hold.expires_at)}</Td>
                  <Td>
                    <HStack>
                      <Badge
                        colorScheme={hold.status === "OPEN" ? "blue" : "gray"}
                      >
                        {hold.status}
                      </Badge>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Collapse>
      </Box>
      {/* Release Confirmation Dialog */}
      <AlertDialog
        isOpen={releaseDialog.open}
        leastDestructiveRef={undefined}
        onClose={() => {
          if (!releaseLoading) setReleaseDialog({ open: false });
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Release Hold
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to release this hold?
              <br />
              <b>{releaseDialog.hold?.name}</b>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={() => setReleaseDialog({ open: false })}
                disabled={releaseLoading}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={releaseLoading}
                disabled={releaseLoading}
                onClick={async () => {
                  if (onReleaseHold && releaseDialog.hold) {
                    setReleaseLoading(true);
                    try {
                      await onReleaseHold(releaseDialog.hold);
                    } finally {
                      setReleaseLoading(false);
                      setReleaseDialog({ open: false });
                    }
                  }
                }}
              >
                Release
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TicketHoldsTable;
